import { useEffect, useState } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Chip, Collapse, Paper, Stack, Typography } from "@mui/material";
import { UpsertAxleDataMutation } from "../../../types";

type ProfileConflict = UpsertAxleDataMutation["upsertAxleData"]["profileConflicts"][number];

type ProfileConflictsAuditProps = {
  conflicts: ReadonlyArray<ProfileConflict>;
};

export const ProfileConflictsAudit = ({ conflicts }: ProfileConflictsAuditProps) => {
  const [showAudit, setShowAudit] = useState(true);

  useEffect(() => {
    // New result with conflicts: expand so issues aren't missed.
    setShowAudit(conflicts.length > 0);
  }, [conflicts.length]);

  if (conflicts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h3">
          Audit · profile conflicts ({conflicts.length})
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowAudit(prev => !prev)}
          endIcon={showAudit ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showAudit ? "Hide audit" : "Show audit"}
        </Button>
      </Stack>

      <Collapse in={showAudit}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These would fail the axle_person_document_id unique constraint. Dry run checks Nutrition duplicates and reads
          existing Axle person documents (no upsert) to find emails/profileIds that already resolve to a TWRN owned by
          another membership. Live upsert can still surface additional mapping collisions from Axle&apos;s response.
        </Typography>
        <Stack spacing={2}>
          {conflicts.map((conflict, index) => (
            <Paper key={`${conflict.kind}-${conflict.email ?? ""}-${index}`} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
                <Chip label={conflict.kind} size="small" color="warning" />
                {conflict.orgName && <Chip label={conflict.orgName} size="small" variant="outlined" />}
                {conflict.email && <Chip label={conflict.email} size="small" variant="outlined" />}
              </Stack>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {conflict.detail}
              </Typography>
              {conflict.axlePersonDocumentId && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Axle person document: {conflict.axlePersonDocumentId}
                </Typography>
              )}
              <Stack spacing={0.5}>
                {conflict.memberships.map(membership => (
                  <Typography key={membership.orgMembershipId} variant="body2" color="text.secondary">
                    {[membership.givenName, membership.familyName].filter(Boolean).join(" ") || "(no name)"}
                    {membership.email ? ` · ${membership.email}` : ""}
                    {membership.profileKind ? ` · ${membership.profileKind}` : ""}
                    {membership.orgName ? ` · ${membership.orgName}` : ""}
                    {` · ${membership.orgMembershipId}`}
                    {membership.existingAxlePersonDocumentId
                      ? ` · existing doc ${membership.existingAxlePersonDocumentId}`
                      : ""}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};
